import { Fragment, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { ROLES } from "../lib/constants";
import { I } from "./primitives";

// ─────────────────────────────────────────────────────────────────────────────
// Geo-IP lookup helpers — used by AddServerModal & EditServerModal to suggest
// a region from the host the operator typed. The actual geo-IP call is proxied
// through the hub backend (GET /api/v1/dashboard/geo/:host) to avoid CORS
// restrictions that free geo-IP APIs impose on browser requests.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true for IP addresses we shouldn't bother geo-locating: loopback,
 * RFC1918 private space, link-local, and the unspecified address. For these
 * we default to a "local" label since the public geo-IP would otherwise
 * return the dashboard user's egress IP, which is wrong.
 */
function isPrivateOrLocalHost(host) {
  const h = host.trim().toLowerCase();
  if (!h) return false;
  if (h === "localhost" || h === "::1" || h === "0.0.0.0") return true;
  // IPv4: 127/8, 10/8, 192.168/16, 172.16/12, 169.254/16 (link-local).
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (m) {
    const [a, b] = [parseInt(m[1], 10), parseInt(m[2], 10)];
    if (a === 127 || a === 10 || a === 0) return true;
    if (a === 192 && b === 168) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 169 && b === 254) return true;
  }
  return false;
}

/**
 * Look up the geo for a host via the hub's server-side proxy. Returns a short
 * region string like "Singapore, SG" (city + country_code) or null if the
 * lookup fails or the host is private.
 */
async function lookupRegion(host, signal) {
  if (isPrivateOrLocalHost(host)) return "local";
  try {
    const data = await api.geoLookup(host);
    if (!data?.success || !data.region) return null;
    return data.region;
  } catch {
    return null;
  }
}

/**
 * Add-server modal — closed-loop via the hub's enrollment endpoint.
 *
 * Step 1 — collect server identity (name, host, port, tag, region).
 * Step 2 — POST /api/v1/dashboard/servers returns { server, token }. We show
 *          the install command with the REAL token and poll `liveServers`
 *          for the new id; when `lastSeen` flips non-null the modal advances
 *          to "connected" automatically.
 *
 * `liveServers` is passed from App (the result of useServers polling). We
 * watch for the enrolled server's `lastSeen` to detect first contact.
 */
export function AddServerModal({ onClose, liveServers = [] }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    host: "",
    port: "8745",
    tag: "",
    region: "",
    regionManual: false,
    advanced: false,
    useCreds: false,
    username: "deploy",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [regionDetect, setRegionDetect] = useState("idle");
  const [enrolling, setEnrolling] = useState(false);

  // Filled by POST /api/v1/dashboard/servers
  const [enrolledServerId, setEnrolledServerId] = useState(null);
  const [enrolledToken, setEnrolledToken] = useState(null);

  const existingTags = useMemo(() => ROLES.map((r) => r.tag), []);

  // Geo-IP lookup whenever the host changes — but only when the operator
  // hasn't already typed a manual region (`regionManual`). Debounced 500ms
  // to avoid hitting the API on every keystroke. AbortController cancels
  // the in-flight request when the host changes mid-lookup.
  useEffect(() => {
    if (form.regionManual) return;
    if (!form.host.trim()) {
      setRegionDetect("idle");
      setForm((f) => ({ ...f, region: "" }));
      return;
    }
    setRegionDetect("detecting");
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const region = await lookupRegion(form.host.trim(), controller.signal);
        // Re-check regionManual at resolve time — operator may have typed
        // something while the request was pending.
        setForm((f) => (f.regionManual ? f : { ...f, region: region ?? "" }));
        setRegionDetect(region ? "done" : "failed");
      } catch (err) {
        if (err.name === "AbortError") return;
        setRegionDetect("failed");
      }
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.host, form.regionManual]);

  const [copyState, setCopyState] = useState(null);

  // Derived connection state: poll liveServers (refreshed every 3s by App)
  // for the enrolled server's lastSeen.
  const connState = useMemo(() => {
    if (!enrolledServerId) return "idle";
    const target = liveServers.find((s) => s.id === enrolledServerId);
    if (target && target.lastSeen) return "connected";
    return "waiting";
  }, [enrolledServerId, liveServers]);

  const connectedAgent = useMemo(() => {
    if (connState !== "connected") return null;
    return liveServers.find((s) => s.id === enrolledServerId) ?? null;
  }, [connState, enrolledServerId, liveServers]);

  const submitStep1 = async (e) => {
    e?.preventDefault();
    const clientErrs = {};
    if (!form.name.trim()) clientErrs.name = "Nama server wajib diisi";
    else if (!/^[a-zA-Z0-9._-]+$/.test(form.name.trim()))
      clientErrs.name = "Hanya huruf, angka, titik, dash, underscore";
    if (!form.host.trim()) clientErrs.host = "IP atau hostname wajib diisi";
    if (!form.tag.trim()) clientErrs.tag = "Tag wajib diisi";
    if (!form.region.trim()) clientErrs.region = "Region wajib diisi";
    if (Object.keys(clientErrs).length) {
      setErrors(clientErrs);
      return;
    }
    setErrors({});
    setEnrolling(true);
    try {
      const res = await api.createServer({
        name: form.name.trim(),
        host: form.host.trim(),
        port: Number.parseInt(form.port, 10) || 8745,
        tag: form.tag.trim(),
        region: form.region,
      });
      setEnrolledServerId(res.server.id);
      setEnrolledToken(res.token);
      setStep(2);
    } catch (err) {
      if (err.status === 409) {
        setErrors({ name: "Nama server sudah dipakai" });
      } else if (err.status === 422 && err.body?.issues?.fieldErrors) {
        const issues = err.body.issues.fieldErrors;
        const errs = {};
        for (const [k, v] of Object.entries(issues)) if (v?.[0]) errs[k] = v[0];
        setErrors(errs);
      } else {
        setErrors({ _global: err.message || "Gagal mendaftarkan server" });
      }
    } finally {
      setEnrolling(false);
    }
  };

  // One-shot install command. Downloads the agent binary from THIS hub
  // (via the public /api/v1/agent/install.sh endpoint) and pipes it into bash
  // with the token + hostname passed via env. The token never appears in URL
  // or shell history that way.
  const installCommand = enrolledToken
    ? `curl -fsSL ${window.location.origin}/api/v1/agent/install.sh \\
  | sudo MONIBOT_HUB_URL=${window.location.origin} \\
         MONIBOT_TOKEN=${enrolledToken} \\
         MONIBOT_HOSTNAME=${form.name} \\
         bash`
    : null;

  const copyCommand = () => {
    if (!installCommand) return;
    try {
      navigator.clipboard?.writeText(installCommand);
      setCopyState("ok");
      setTimeout(() => setCopyState(null), 1600);
    } catch {
      setCopyState("err");
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 640 }}>
        <div className="modal-head">
          <div className="flex items-center" style={{ gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="modal-title">Tambah Server</div>
              <div className="modal-sub">
                {step === 1
                  ? "Mulai dengan detail server, lalu instal agent."
                  : "Jalankan perintah di server target — agent akan menghubungi sendiri."}
              </div>
            </div>
            <button className="btn-icon bare" onClick={onClose} title="Tutup"><I.close /></button>
          </div>

          <div className="stepper" role="list">
            <button
              className="step"
              data-active={step === 1}
              data-done={step > 1}
              type="button"
              onClick={() => step > 1 && setStep(1)}
              disabled={step === 1}
            >
              <span className="step-dot">{step > 1 ? <I.check /> : "1"}</span>
              <span className="step-label">Detail Server</span>
            </button>
            <span className="step-bar" data-done={step > 1} />
            <div className="step" data-active={step === 2} role="listitem">
              <span className="step-dot">2</span>
              <span className="step-label">Instal Agent</span>
            </div>
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={submitStep1}>
            <div className="modal-body">
              {errors._global && (
                <div
                  className="field-hint"
                  style={{
                    color: "var(--crit)",
                    background: "var(--crit-soft)",
                    padding: "8px 12px",
                    borderRadius: 8,
                    marginBottom: 12,
                  }}
                >
                  {errors._global}
                </div>
              )}

              <div className="field">
                <label className="field-label">Nama Server</label>
                <div className="input">
                  <input
                    autoFocus
                    placeholder="web-05"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                {errors.name && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.name}</div>}
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Alamat IP / Hostname</label>
                  <div className="input">
                    <input
                      placeholder="10.0.4.21 atau host.example.com"
                      value={form.host}
                      onChange={(e) => setForm({ ...form, host: e.target.value })}
                      style={{ fontFamily: "var(--font-mono)" }}
                    />
                  </div>
                  {errors.host && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.host}</div>}
                </div>
                <div className="field">
                  <label className="field-label">Port Agent</label>
                  <div className="input">
                    <input
                      placeholder="8745"
                      value={form.port}
                      onChange={(e) => setForm({ ...form, port: e.target.value })}
                      style={{ fontFamily: "var(--font-mono)" }}
                    />
                  </div>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Tag / Role</label>
                  <div className="input">
                    <input
                      placeholder="web, api, db, atau custom…"
                      value={form.tag}
                      onChange={(e) => setForm({ ...form, tag: e.target.value })}
                      list="role-suggestions"
                      style={{ fontFamily: "var(--font-mono)" }}
                    />
                    <datalist id="role-suggestions">
                      {existingTags.map((t) => <option key={t} value={t} />)}
                    </datalist>
                  </div>
                  <div className="tag-suggest">
                    {existingTags.slice(0, 6).map((t) => (
                      <button
                        type="button"
                        key={t}
                        className="tag-suggest-chip"
                        data-active={form.tag === t}
                        onClick={() => setForm({ ...form, tag: t })}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {errors.tag && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.tag}</div>}
                </div>
                <div className="field">
                  <label className="field-label">
                    <span>Region</span>
                    {!form.regionManual && regionDetect === "detecting" && (
                      <span className="region-status">
                        <span className="region-spinner" />
                        <span>mendeteksi…</span>
                      </span>
                    )}
                    {!form.regionManual && regionDetect === "done" && form.region && (
                      <span className="region-status" style={{ color: "var(--accent)" }}>
                        <I.check />
                        <span>auto dari IP</span>
                      </span>
                    )}
                    {!form.regionManual && regionDetect === "failed" && (
                      <span className="region-status" style={{ color: "var(--text-3)" }}>
                        ketik manual
                      </span>
                    )}
                    {form.regionManual && (
                      <button
                        type="button"
                        className="region-override-link"
                        onClick={() => {
                          setForm({ ...form, regionManual: false, region: "" });
                          setRegionDetect("idle");
                        }}
                      >
                        gunakan auto
                      </button>
                    )}
                  </label>
                  <div className="input">
                    <input
                      placeholder="Singapore, SG atau ketik manual"
                      value={form.region}
                      onChange={(e) =>
                        setForm({ ...form, region: e.target.value, regionManual: true })
                      }
                      style={{ fontFamily: "var(--font-mono)" }}
                    />
                  </div>
                  {errors.region && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.region}</div>}
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={enrolling}>
                Batal
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={enrolling}
                style={{ opacity: enrolling ? 0.55 : 1 }}
              >
                <span>{enrolling ? "Mendaftarkan…" : "Lanjut: Instal Agent"}</span>
                <I.chevron />
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <Fragment>
            <div className="modal-body" style={{ paddingTop: 4 }}>
              <div className="server-summary">
                <span
                  className="server-status-pip"
                  data-status={connState === "connected" ? "ok" : "offline"}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center gap-2">
                    <span className="server-name">{form.name}</span>
                    <span className="server-tag" data-role={form.tag}>{form.tag}</span>
                  </div>
                  <div className="server-addr">{form.host}{form.port ? ":" + form.port : ""} · {form.region}</div>
                </div>
              </div>

              <div className="cmd-block">
                <div className="cmd-block-head">
                  <span className="cmd-block-prompt">$ shell · Linux / musl</span>
                  <span style={{ flex: 1 }} />
                  <span className="cmd-token" title="Token aktif untuk server ini (ditampilkan sekali)">
                    <span style={{ color: "var(--text-3)" }}>token:</span>{" "}
                    {enrolledToken ? `${enrolledToken.slice(0, 12)}…` : "—"}
                  </span>
                  <button className="btn btn-ghost cmd-copy" onClick={copyCommand} type="button">
                    {copyState === "ok" ? (
                      <Fragment><I.check /><span>Tersalin</span></Fragment>
                    ) : (
                      <Fragment><I.copy /><span>Salin</span></Fragment>
                    )}
                  </button>
                </div>
                <pre className="cmd-body">{installCommand}</pre>
              </div>

              <div className="conn-status" data-state={connState}>
                {connState === "waiting" && (
                  <Fragment>
                    <span className="conn-spinner" />
                    <div style={{ flex: 1 }}>
                      <div className="conn-title">Menunggu agent menghubungi hub…</div>
                      <div className="conn-sub">
                        Jalankan perintah di atas pada{" "}
                        <span className="mono" style={{ color: "var(--text-2)" }}>{form.host}</span>.
                        Hub akan otomatis flag-active begitu push pertama masuk.
                      </div>
                    </div>
                  </Fragment>
                )}
                {connState === "connected" && (
                  <Fragment>
                    <span className="conn-check"><I.check /></span>
                    <div style={{ flex: 1 }}>
                      <div className="conn-title">Agent terhubung dari {connectedAgent?.host ?? form.host}</div>
                      <div className="conn-sub">
                        {connectedAgent?.agentVersion ? (
                          <Fragment>
                            <span className="mono">monibot-agent v{connectedAgent.agentVersion}</span>
                            {connectedAgent.os && <> · {connectedAgent.os}</>}
                            {connectedAgent.cores && <> · {connectedAgent.cores} core</>}
                          </Fragment>
                        ) : (
                          <span className="mono">push pertama diterima</span>
                        )}
                      </div>
                    </div>
                    <span className="status-badge" data-status="ok">Online</span>
                  </Fragment>
                )}
              </div>

              <div className="install-help" style={{ fontSize: 12.5, color: "var(--text-3)" }}>
                <strong style={{ color: "var(--warn)" }}>Penting:</strong>{" "}
                Token hanya muncul sekali. Kalau hilang, hapus server ini dan daftar ulang
                untuk dapat token baru.
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                {connState === "connected" ? "Selesai" : "Instal nanti"}
              </button>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
                disabled={connState !== "connected"}
                style={{
                  opacity: connState === "connected" ? 1 : 0.55,
                  pointerEvents: connState === "connected" ? "auto" : "none",
                }}
              >
                <I.check />
                <span>Selesai</span>
              </button>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
}

export function DeleteModal({ server, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  if (!server) return null;

  const handleConfirm = async () => {
    setDeleting(true);
    setError(null);
    try {
      await onConfirm(server);
    } catch (e) {
      setError(e.message || "Gagal menghapus server");
      setDeleting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
        <div className="modal-head">
          <div className="modal-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--crit)" }}><I.trash /></span>
            Hapus Server
          </div>
          <div className="modal-sub">
            Server akan di-archive: agent tidak bisa push lagi (token revoked), tapi histori
            metrik & log notifikasi tetap tersimpan.
          </div>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>
            Anda akan menghapus <b style={{ fontFamily: "var(--font-mono)" }}>{server.name}</b> ({server.ip}) dari monitoring.
          </p>
          <div className="field" style={{ marginTop: 16 }}>
            <label className="field-label">
              Ketik nama server untuk konfirmasi:{" "}
              <span className="mono" style={{ color: "var(--text)" }}>{server.name}</span>
            </label>
            <div className="input">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={server.name}
                style={{ fontFamily: "var(--font-mono)" }}
                autoFocus
              />
            </div>
          </div>
          {error && (
            <div
              className="field-hint"
              style={{
                color: "var(--crit)",
                marginTop: 8,
                background: "var(--crit-soft)",
                padding: "8px 10px",
                borderRadius: 6,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={deleting}>Batal</button>
          <button
            className="btn btn-danger"
            disabled={text !== server.name || deleting}
            style={{
              opacity: text !== server.name || deleting ? 0.5 : 1,
              pointerEvents: text !== server.name || deleting ? "none" : "auto",
            }}
            onClick={handleConfirm}
          >
            <I.trash />
            <span>{deleting ? "Menghapus…" : "Archive Server"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditServerModal({ server, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: server.name || "",
    host: server.host || server.ip || "",
    port: String(server.port || 8745),
    tag: server.tag || "",
    region: server.region || "",
    regionManual: true, // start as manual since region is pre-filled
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [regionDetect, setRegionDetect] = useState("idle");

  const existingTags = useMemo(() => ROLES.map((r) => r.tag), []);

  // Geo-IP lookup whenever the host changes — only when not in manual mode.
  // Debounced 500ms to avoid hitting the API on every keystroke.
  useEffect(() => {
    if (form.regionManual) return;
    if (!form.host.trim()) {
      setRegionDetect("idle");
      setForm((f) => ({ ...f, region: "" }));
      return;
    }
    setRegionDetect("detecting");
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const region = await lookupRegion(form.host.trim(), controller.signal);
        setForm((f) => (f.regionManual ? f : { ...f, region: region ?? "" }));
        setRegionDetect(region ? "done" : "failed");
      } catch (err) {
        if (err.name === "AbortError") return;
        setRegionDetect("failed");
      }
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.host, form.regionManual]);

  const hasChanges = useMemo(() => {
    return (
      form.name !== (server.name || "") ||
      form.host !== (server.host || server.ip || "") ||
      String(form.port) !== String(server.port || 8745) ||
      form.tag !== (server.tag || "") ||
      form.region !== (server.region || "")
    );
  }, [form, server]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const clientErrs = {};
    if (!form.name.trim()) clientErrs.name = "Nama server wajib diisi";
    else if (!/^[a-zA-Z0-9._-]+$/.test(form.name.trim()))
      clientErrs.name = "Hanya huruf, angka, titik, dash, underscore";
    if (!form.host.trim()) clientErrs.host = "IP atau hostname wajib diisi";
    if (!form.tag.trim()) clientErrs.tag = "Tag wajib diisi";
    if (!form.region.trim()) clientErrs.region = "Region wajib diisi";
    if (Object.keys(clientErrs).length) {
      setErrors(clientErrs);
      return;
    }
    setErrors({});
    setSaving(true);

    // Only send changed fields.
    const patch = {};
    if (form.name.trim() !== (server.name || "")) patch.name = form.name.trim();
    if (form.host.trim() !== (server.host || server.ip || "")) patch.host = form.host.trim();
    const port = Number.parseInt(form.port, 10) || 8745;
    if (port !== (server.port || 8745)) patch.port = port;
    if (form.tag.trim() !== (server.tag || "")) patch.tag = form.tag.trim();
    if (form.region !== (server.region || "")) patch.region = form.region;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    try {
      await api.updateServer(server.id, patch);
      onSaved?.();
      onClose();
    } catch (err) {
      if (err.status === 409) {
        setErrors({ name: "Nama server sudah dipakai" });
      } else if (err.status === 422 && err.body?.issues?.fieldErrors) {
        const issues = err.body.issues.fieldErrors;
        const errs = {};
        for (const [k, v] of Object.entries(issues)) if (v?.[0]) errs[k] = v[0];
        setErrors(errs);
      } else {
        setErrors({ _global: err.message || "Gagal menyimpan perubahan" });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
        <div className="modal-head">
          <div className="flex items-center" style={{ gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="modal-title">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <I.edit /> Edit Server
                </span>
              </div>
              <div className="modal-sub">
                Ubah metadata server <span className="mono">{server.name}</span>.
              </div>
            </div>
            <button className="btn-icon bare" onClick={onClose} title="Tutup"><I.close /></button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {errors._global && (
              <div
                className="field-hint"
                style={{
                  color: "var(--crit)",
                  background: "var(--crit-soft)",
                  padding: "8px 12px",
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              >
                {errors._global}
              </div>
            )}

            <div className="field">
              <label className="field-label">Nama Server</label>
              <div className="input">
                <input
                  autoFocus
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              {errors.name && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.name}</div>}
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Alamat IP / Hostname</label>
                <div className="input">
                  <input
                    value={form.host}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    style={{ fontFamily: "var(--font-mono)" }}
                  />
                </div>
                {errors.host && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.host}</div>}
              </div>
              <div className="field">
                <label className="field-label">Port</label>
                <div className="input">
                  <input
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: e.target.value })}
                    style={{ fontFamily: "var(--font-mono)" }}
                  />
                </div>
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Tag / Role</label>
                <div className="input">
                  <input
                    value={form.tag}
                    onChange={(e) => setForm({ ...form, tag: e.target.value })}
                    list="edit-role-suggestions"
                    style={{ fontFamily: "var(--font-mono)" }}
                  />
                  <datalist id="edit-role-suggestions">
                    {existingTags.map((t) => <option key={t} value={t} />)}
                  </datalist>
                </div>
                <div className="tag-suggest">
                  {existingTags.slice(0, 6).map((t) => (
                    <button
                      type="button"
                      key={t}
                      className="tag-suggest-chip"
                      data-active={form.tag === t}
                      onClick={() => setForm({ ...form, tag: t })}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {errors.tag && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.tag}</div>}
              </div>
              <div className="field">
                <label className="field-label">
                  <span>Region</span>
                  {!form.regionManual && regionDetect === "detecting" && (
                    <span className="region-status">
                      <span className="region-spinner" />
                      <span>mendeteksi…</span>
                    </span>
                  )}
                  {!form.regionManual && regionDetect === "done" && form.region && (
                    <span className="region-status" style={{ color: "var(--accent)" }}>
                      <I.check />
                      <span>auto dari IP</span>
                    </span>
                  )}
                  {!form.regionManual && regionDetect === "failed" && (
                    <span className="region-status" style={{ color: "var(--text-3)" }}>
                      ketik manual
                    </span>
                  )}
                  {form.regionManual && (
                    <button
                      type="button"
                      className="region-override-link"
                      onClick={() => {
                        setForm({ ...form, regionManual: false, region: "" });
                        setRegionDetect("idle");
                      }}
                    >
                      deteksi dari IP
                    </button>
                  )}
                </label>
                <div className="input">
                  <input
                    placeholder="Singapore, SG atau ketik manual"
                    value={form.region}
                    onChange={(e) =>
                      setForm({ ...form, region: e.target.value, regionManual: true })
                    }
                    style={{ fontFamily: "var(--font-mono)" }}
                  />
                </div>
                {errors.region && <div className="field-hint" style={{ color: "var(--crit)" }}>{errors.region}</div>}
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Batal
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !hasChanges}
              style={{ opacity: saving || !hasChanges ? 0.55 : 1 }}
            >
              <span>{saving ? "Menyimpan…" : "Simpan"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
